import type Highcharts from "highcharts";
import type {
  AssetRegisterAggregatedTimeSeriesResponse,
  AssetRegisterItemAggregatedTimeSeries,
} from "kubb";
import type React from "react";
import { usePrivacyMode } from "../../reducers/privacy";
import { ErrorBanner } from "../Error/ErrorBanner.tsx";
import {
  type AggregatedChartsAmendedOptions,
  type AggregatedDataItemClickHandlerProps,
  type AssetRegisterItemColumn,
  type AssetRegisterItemPoint,
  aggregationDataItemClickHandler,
} from "./AggregationChartFunctions";
import Chart from "./Chart";

import { round } from "lodash-es";

export default function AggregationDistributionChart(
  props: {
    data?: AssetRegisterAggregatedTimeSeriesResponse;
    aggregation: string;
    parameter: string;
    loading: boolean;
    error?: Error;
    privacyModeEnabled?: boolean;
    name?: string;
    unit: string;
    height?: string;
  } & AggregatedDataItemClickHandlerProps,
): React.ReactElement {
  const { height = "400px" } = props;
  const privacyMode = usePrivacyMode();

  const aggregation = props.aggregation;
  const parameter = props.parameter;

  const dataMapper = (
    v: AssetRegisterItemAggregatedTimeSeries,
  ): AssetRegisterItemColumn => {
    return {
      asset_position_id: v.asset_position_id,
      assetPositionReference: v.asset_position_reference,
      locationAddress: v.location_address,
      asset_id: v.asset_id,
      y: v.aggregations[parameter]?.[aggregation]?.[0] || undefined,
    };
  };

  const seriesDefinition = (
    data: AssetRegisterAggregatedTimeSeriesResponse,
    name?: string,
    userRef?: string,
    color?: string,
  ): Highcharts.SeriesColumnOptions & AggregatedChartsAmendedOptions => {
    return {
      id: userRef,
      data: data.data
        .map(dataMapper)
        .sort((n1, n2) => (n1.y || 0) - (n2.y || 0)),
      type: "column",
      color: color ? `var(--${color})` : undefined,
      animation: false,
      events: {
        click: aggregationDataItemClickHandler({
          start_time: props.start_time,
          end_time: props.end_time,
          privacyMode: privacyMode,
          company_id: props.company_id,
          scheme_id: props.scheme_id,
        }),
      },
      turboThreshold: 0,
      tooltip: {
        pointFormatter: function (this: AssetRegisterItemPoint) {
          const value = this.assetPositionReference
            ? `${this.locationAddress} - ${this.assetPositionReference}`
            : this.locationAddress;
          return (
            `<tspan style='fill:${this.color}'>●</tspan>` +
            `<tspan style='${privacyMode && "filter: url(#blur)"}'>${value}</tspan>` +
            `<tspan style='font-weight:bold'> ${round(this.y as number, 1)}${
              props.unit
            } </tspan>`
          );
        },
      },
    };
  };

  const series: Highcharts.SeriesColumnOptions[] = props.data
    ? [seriesDefinition(props.data)]
    : [];

  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    chart: {
      type: "column",
      height: height,
    },
    // xAxis: {
    //   type: "datetime",
    //   min: props.params?.startDatetime?.valueOf(),
    //   max: props.params?.endDatetime?.valueOf(),
    // },
    series: series,
    yAxis: props.data
      ? [
          {
            labels: {
              format: `{value}${props.unit}`,
            },
            title: {
              text: props.name,
            },
          },
        ]
      : [],
    legend: {
      enabled: false,
    },
    tooltip: {
      shared: true,
      // valueSuffix: props.unit,
      // valueDecimals: props.rounding,
      // pointFormat: `{series.name} {point.y}${props.unit}`
    },
  };

  if (props.error) {
    return <ErrorBanner error={props.error} />;
  }

  return (
    <Chart
      options={options}
      loading={props.loading}
      privacyMode={privacyMode}
    />
  );
}
